import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Eraser, Check, PenNib, X } from "@phosphor-icons/react";

export function SlotFirma({ label, ruolo, firma, onFirma, onRimuovi }) {
  return (
    <div className="rounded-md border border-slate-200 p-3 space-y-2" data-testid={`firma-slot-${ruolo}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
      {firma ? (
        <div className="relative rounded-md bg-slate-50 border border-slate-100 p-2">
          <img src={firma} alt={`Firma ${label}`} className="h-20 w-full object-contain" data-testid={`firma-img-${ruolo}`} />
          <Button variant="ghost" size="sm" className="absolute top-1 right-1 h-7 w-7 p-0" onClick={onRimuovi} data-testid={`firma-rimuovi-${ruolo}`}>
            <X size={14} className="text-red-600" />
          </Button>
        </div>
      ) : (
        <Button variant="outline" className="w-full h-20 border-dashed" onClick={onFirma} data-testid={`firma-btn-${ruolo}`}>
          <PenNib size={16} /> Firma su tablet
        </Button>
      )}
    </div>
  );
}

export default function SignaturePad({ open, onOpenChange, titolo, sottotitolo, onConfirm }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [vuota, setVuota] = useState(true);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const c = canvasRef.current;
      if (!c) return;
      const dpr = window.devicePixelRatio || 1;
      const rect = c.getBoundingClientRect();
      c.width = rect.width * dpr;
      c.height = rect.height * dpr;
      const ctx = c.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.lineWidth = 2.4;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#0f172a";
      setVuota(true);
    }, 50);
    return () => clearTimeout(t);
  }, [open]);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
    canvasRef.current.setPointerCapture?.(e.pointerId);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (vuota) setVuota(false);
  };

  const end = () => { drawing.current = false; };

  const pulisci = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.restore();
    setVuota(true);
  };

  const conferma = () => {
    onConfirm(canvasRef.current.toDataURL("image/png"));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="signature-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><PenNib size={18} /> {titolo}</DialogTitle>
          <DialogDescription>{sottotitolo || "Firmare con il dito o con la penna all'interno del riquadro."}</DialogDescription>
        </DialogHeader>
        <div className="relative rounded-md border-2 border-dashed border-slate-300 bg-white">
          <canvas
            ref={canvasRef}
            className="w-full h-56 sm:h-64 touch-none cursor-crosshair rounded-md"
            style={{ touchAction: "none" }}
            onPointerDown={start}
            onPointerMove={move}
            onPointerUp={end}
            onPointerLeave={end}
            onPointerCancel={end}
            data-testid="signature-canvas"
          />
          <div className="absolute left-6 right-6 bottom-10 border-b border-slate-300 pointer-events-none" />
          <div className="absolute left-6 bottom-3 text-[10px] font-mono uppercase tracking-widest text-slate-400 pointer-events-none">Firma</div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={pulisci} data-testid="signature-clear-btn">
            <Eraser size={14} /> Cancella
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="signature-cancel-btn">Annulla</Button>
            <Button size="sm" onClick={conferma} disabled={vuota} data-testid="signature-confirm-btn">
              <Check size={14} /> Conferma firma
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
